import ShellLink from '../common/ShellLink';
import type { ReaderSubject } from '../../types';

/**
 * Which subjects read this room. READ-ONLY, deliberately.
 *
 * The link is chosen from the SUBJECT — you are looking at a thing you are
 * about to write about and deciding what research should back it — so the room
 * reports its readers rather than picking them. There is one control for this
 * and it is not here.
 *
 * Plural because a room maps a market and several subjects can be cut out of
 * one market. The room used to name exactly one subject, which meant the second
 * subject to want these findings could only be served by taking them from the
 * first.
 *
 * WHAT THE LINK DOES, and why it is worth saying on screen: while it is set,
 * those subjects read this room LIVE. Nothing is copied. A page written under
 * one of them can ask what this room found and sees it as it is now, not as it
 * was when the link was made — so the connection is not a hand-off you
 * complete. The room keeps working, and they keep reading it.
 */

interface Props {
  subjects: ReaderSubject[];
}

export default function ReaderSubjects({ subjects }: Props) {
  // Nothing at all when nothing reads it. Most rooms feed no subject, and a
  // label saying so would be a permanent piece of furniture reporting the
  // absence of something the room does not need in order to be useful.
  if (!subjects.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span
        className="text-gray-500 dark:text-gray-400"
        title="These subjects read this room live - nothing is copied. Change this from the subject."
      >
        Read by
      </span>
      {subjects.map((s, i) => (
        <span key={s.id} className="flex items-center gap-2">
          {/* A link, not a label: having seen the name, the next thing you want
              is to go and look at it.

              ShellLink, not Link: a subject is Publish and this room is
              Boardroom, so a router link would render a Publish workspace at
              boardroom.lumnian.com — the wrong nav, and a URL nobody could
              share. See shell/routeShell.ts. */}
          <ShellLink
            to={`/subjects/${s.id}`}
            className="font-medium text-blue-700 dark:text-blue-300 hover:underline"
          >
            {s.name}
          </ShellLink>
          {i < subjects.length - 1 && (
            <span className="text-gray-300 dark:text-gray-600">·</span>
          )}
        </span>
      ))}
    </div>
  );
}
